import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const iconButtonVariants = cva(
  'inline-flex cursor-pointer items-center rounded-full justify-center whitespace-nowrap ring-offset-white transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none  font-nunito700 select-none [&_svg]:pointer-events-none [&_svg]:shrink-0 gap-[0.25rem]',
  {
    variants: {
      variant: {
        primary:
          'bg-warp-green-300 duration-0 text-black hover:bg-none hover:bg-warp-green-400 active:bg-warp-green-500 disabled:bg-light-grey-300 disabled:text-dark-grey-100 disabled:font-nunito700',
        secondary:
          'text-deep-teal-700 border border-deep-teal-700 active:bg-deep-teal-50 disabled:text-grey-500 disabled:border-grey-100',
        link: 'text-green-500 hover:text-green-700 active:text-green-900 disabled:text-grey-500 ',
        grey: 'bg-light-grey-300 text-black hover:bg-light-grey-400 active:bg-light-grey-400',
      },
      size: {
        large: 'size-16 px-4 py-spacing-[0.93rem] [&_svg]:size-6',
        medium: 'size-[3.25rem] px-4 py-spacing-[0.93rem] [&_svg]:size-6',
        small: 'size-10 px-4 py-spacing-[0.93rem] [&_svg]:size-[1.25rem]',
        extraSmall: 'size-8 px-4 py-spacing-[0.93rem] [&_svg]:size-[1.25rem]',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'medium',
    },
  }
);

export interface IconButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof iconButtonVariants> {
  asChild?: boolean;
}

const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(iconButtonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
IconButton.displayName = 'IconButton';

export { IconButton, iconButtonVariants };
